import { IconButton, Link, Stack, Typography } from '@mui/material'
import { SourceDocument, TwinNoteSummary } from 'api/model'
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { useState } from 'react'
import { COLORS } from 'app-constants'

const LinkedDocumentRow = ({
  doc,
  setSourceDocuments,
}: {
  doc: SourceDocument
  setSourceDocuments: React.Dispatch<React.SetStateAction<SourceDocument[]>>
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false)
  const onMouseEnter = () => {
    setIsHovered(true)
  }
  const onMouseLeave = () => {
    setIsHovered(false)
  }

  const onDeleteDocument = async () => {
    setSourceDocuments((prev) => {
      return prev.filter((d) => d.document_id !== doc.document_id)
    })
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      width="100%"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      height="32px">
      <DescriptionOutlinedIcon
        sx={{
          height: '16px',
          width: '16px',
          fill: COLORS.GRAY_2,
        }}
      />

      <Link
        href={doc.url}
        underline="hover"
        target="_blank"
        rel="noreferrer"
        sx={{
          flex: '1',
          ml: 1,
          width: '100%',
        }}>
        <Typography
          variant="pXS"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: COLORS.GRAY_2,
          }}>
          {doc.name}
        </Typography>
      </Link>

      {isHovered && (
        <IconButton onClick={onDeleteDocument}>
          <DeleteOutlinedIcon
            sx={{
              height: '16px',
              width: '16px',
              fill: COLORS.GRAY_2,
            }}
          />
        </IconButton>
      )}
    </Stack>
  )
}

const NoteRow = ({
  note,
  setNotes,
}: {
  note: TwinNoteSummary
  setNotes: React.Dispatch<React.SetStateAction<TwinNoteSummary[]>>
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false)
  const onMouseEnter = () => {
    setIsHovered(true)
  }
  const onMouseLeave = () => {
    setIsHovered(false)
  }

  const onDeleteNoteClick = () => {
    setNotes((prev) => {
      return prev.filter((n) => n.id !== note.id)
    })
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      height="32px"
      width="100%">
      <Stack direction="row" alignItems="center" spacing={1} width="100%">
        <TextSnippetOutlinedIcon
          sx={{
            height: '16px',
            width: '16px',
            fill: COLORS.GRAY_2,
          }}
        />
        <Typography
          variant="pXS"
          ml={1}
          sx={{
            color: COLORS.GRAY_2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {note.title}
        </Typography>
      </Stack>

      {isHovered && (
        <IconButton onClick={onDeleteNoteClick}>
          <DeleteOutlinedIcon
            sx={{
              height: '16px',
              width: '16px',
              fill: COLORS.GRAY_2,
            }}
          />
        </IconButton>
      )}
    </Stack>
  )
}

const AttachedDocumentsList = ({
  sourceDocuments,
  notes,
  setNotes,
  setSourceDocuments,
}: {
  sourceDocuments: SourceDocument[]
  notes: TwinNoteSummary[]
  setNotes: React.Dispatch<React.SetStateAction<TwinNoteSummary[]>>
  setSourceDocuments: React.Dispatch<React.SetStateAction<SourceDocument[]>>
}) => {
  return (
    <Stack mt={1} width="100%">
      {sourceDocuments.length > 0 && (
        <Stack width="100%">
          {sourceDocuments.map((doc) => {
            return <LinkedDocumentRow key={doc.document_id} doc={doc} setSourceDocuments={setSourceDocuments} />
          })}
        </Stack>
      )}
      {notes.length > 0 && (
        <Stack width="100%">
          {notes.map((note) => {
            return <NoteRow key={note.id} note={note} setNotes={setNotes} />
          })}
        </Stack>
      )}
    </Stack>
  )
}

export default AttachedDocumentsList
